import { bloom, layer, risingChord, sparkle } from "@/audio/core/synthArchetypes";
import { layersTotalDurationMs } from "@/audio/core/types";
import type { CandidateLayer } from "@/audio/core/types";
import { LOCKED_LEVEL_COMPLETE_CORE } from "./lockedCandidates";

/**
 * Level completion's star-fill notes and record accents - shared archetype
 * output scoped to the approved L3 core, moved here from
 * `src/audio/lab/round2/completionCandidates.ts`'s `buildCompletionAudition`
 * so production's `AudioManager` and the QA lab's Final Palette Validation
 * matrix both build the exact same accents from one place.
 */

export const STAR_COUNTS = [1, 2, 3] as const;
export type StarCount = (typeof STAR_COUNTS)[number];

export const RECORD_COMBOS = ["none", "starRecord", "highScore", "both"] as const;
export type RecordCombo = (typeof RECORD_COMBOS)[number];

/** Minimum gap between the star-fill notes ending and a record accent, and between the two record accents themselves - so nothing ever collides into an indistinguishable blur. */
export const COMPLETION_MIN_GAP_MS = 400;

const STAR_FILL_SPACING_MS = 220;
const STAR_FILL_SEMITONES = [0, 4, 7] as const;

/** Exported so the QA lab's Round 2 L1/L2/L3 audition matrix (`round2/completionCandidates.ts`) can build the exact same star-fill notes for a candidate core the lab itself still owns, without redefining their synthesis parameters. */
export function starFillLayers(startAtMs: number, count: StarCount): CandidateLayer[] {
  return Array.from({ length: count }, (_, i) => layer(startAtMs + i * STAR_FILL_SPACING_MS, sparkle(STAR_FILL_SEMITONES[i], 160, 0.4 + i * 0.05), `Star fill ${i + 1}`));
}

/** Exported for the same reason as `starFillLayers` above. */
export function recordLayers(startAtMs: number, combo: RecordCombo): CandidateLayer[] {
  if (combo === "none") return [];
  const starRecord = layer(startAtMs, bloom([9, 14], 380, 0.6, -0.15), "NEW STAR RECORD!");
  const highScore = layer(startAtMs + (combo === "both" ? COMPLETION_MIN_GAP_MS + 150 : 0), risingChord([11, 16, 19], 420, 0.68, 0.15), "NEW HIGH SCORE!");
  if (combo === "starRecord") return [starRecord];
  if (combo === "highScore") return [highScore];
  return [starRecord, highScore];
}

/**
 * Assembles the full completion phrase (L3 core + `stars` ascending
 * star-fill notes + whichever record accent combo is selected) - never
 * colliding (see `COMPLETION_MIN_GAP_MS`). NOT what real gameplay plays
 * any more (see `buildLevelSummaryAudio()` below, QA follow-up) - kept
 * only so `finalMapping.ts` can extract the individual `starFill`/
 * `newStarRecord`/`newHighScore` layers by label for standalone QA
 * audition, and so the QA lab's own Round 2 completion matrix
 * (`round2/completionCandidates.ts`) can still let a listener audition
 * the full historical star-fill+record combination it always could.
 */
export function buildCompletionSequence(stars: StarCount, combo: RecordCombo): readonly CandidateLayer[] {
  const coreEndMs = layersTotalDurationMs(LOCKED_LEVEL_COMPLETE_CORE);
  const starLayers = starFillLayers(coreEndMs + 120, stars);
  const starsEndMs = starLayers.length > 0 ? Math.max(...starLayers.map((l) => l.atMs + l.synth.durationMs)) : coreEndMs;
  const records = recordLayers(starsEndMs + COMPLETION_MIN_GAP_MS, combo);
  return [...LOCKED_LEVEL_COMPLETE_CORE, ...starLayers, ...records];
}

/**
 * QA follow-up: the REAL Level Summary sequence `AudioManager.
 * onLevelComplete()` actually schedules - manual playtesting found the
 * ordinary star-fill ticks (up to 3 rapid `sparkle` accents, ~220ms
 * apart, right after the base cue) and a "both records" run (NEW STAR
 * RECORD! immediately followed by NEW HIGH SCORE!, only ~550ms apart -
 * which happens on literally every level's FIRST-EVER completion, since
 * `previous.stars`/`hasPrevious` both start at their "no record yet"
 * default) were the actual source of the reported "cluster of sounds,"
 * not the base cue itself. Per the revised policy: the base cue, then AT
 * MOST one award accent - never a sound per star-fill tick, never both
 * record accents stacked.
 *
 * When both are true, NEW HIGH SCORE (a personal best on the
 * comprehensive Final Score V2 - bite score + skill score + pace bonus)
 * outranks NEW STAR RECORD (a coarser 3-tier star-count personal best)
 * - matching the two accents' own already-authored relative loudness
 * (0.68 vs 0.6). A replay that raises neither plays only the base cue.
 */
export function buildLevelSummaryAudio(combo: RecordCombo): readonly CandidateLayer[] {
  const coreEndMs = layersTotalDurationMs(LOCKED_LEVEL_COMPLETE_CORE);
  const arbitratedCombo: Exclude<RecordCombo, "both"> = combo === "both" ? "highScore" : combo;
  const award = recordLayers(coreEndMs + COMPLETION_MIN_GAP_MS, arbitratedCombo);
  return [...LOCKED_LEVEL_COMPLETE_CORE, ...award];
}
